import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, Download, RefreshCw } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(10);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // Récupérer les paramètres de l'URL
  const orderId = searchParams.get('order_id') || 
                  searchParams.get('orderId') || 
                  searchParams.get('orderid');
  const amount = searchParams.get('amount');
  const txnid = searchParams.get('txnid');

  // Vérifier le statut réel du paiement
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!orderId) {
        setError('Aucun numéro de commande trouvé');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Vérification du statut pour:', orderId);
        
        const response = await fetch(
          `https://api.tdidigital.io/fbapi/api/orange-money/status/${orderId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        console.log('📥 Statut reçu:', data);

        setPaymentStatus(data);
        setLoading(false);

        // Si le paiement a échoué, rediriger vers la page d'annulation
        if (data.status === 'failed' || data.status === 'FAILED') {
          setTimeout(() => {
            navigate('/payment/cancel?reason=Paiement échoué&order_id=' + orderId);
          }, 2000);
        }

      } catch (err) {
        console.error('❌ Erreur vérification statut:', err);
        setError('Impossible de vérifier le statut du paiement');
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [orderId, navigate]);

  // Redirection automatique pour les paiements réussis
  useEffect(() => {
    if (!loading && paymentStatus?.status === 'completed') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/dashboard');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, paymentStatus, navigate]);

  const handleGoToDashboard = () => {
    navigate('/');
  };

  const handleRetry = () => {
    navigate('/subscription');
  };

  const handleViewInvoice = () => {
    alert('Fonctionnalité de facture à venir !');
  };

  // ÉCRAN DE CHARGEMENT
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <Loader2 size={60} className="text-purple-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Vérification du paiement...
          </h2>
          <p className="text-gray-600">
            Nous vérifions le statut de votre transaction Orange Money
          </p>
          {orderId && (
            <p className="text-xs text-gray-400 mt-4 font-mono">
              Commande: {orderId}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ÉCRAN D'ERREUR
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 via-pink-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <XCircle size={60} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Erreur de vérification
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleRetry}
            className="bg-purple-600 text-white font-bold py-3 px-6 rounded-xl hover:bg-purple-700 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw size={20} />
            Retour aux abonnements
          </button>
        </div>
      </div>
    );
  }

  // PAIEMENT EN ATTENTE (pending)
  if (paymentStatus?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-500 via-orange-500 to-red-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <Loader2 size={60} className="text-orange-500 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Paiement en cours...
          </h2>
          <p className="text-gray-600 mb-6">
            Votre paiement Orange Money est en cours de traitement
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-left">
            <p className="text-sm text-yellow-700">
              ⏳ Cela peut prendre quelques instants. Veuillez patienter...
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-orange-600 text-white font-bold py-3 px-6 rounded-xl hover:bg-orange-700 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw size={20} />
            Rafraîchir le statut
          </button>
        </div>
      </div>
    );
  }

  // PAIEMENT RÉUSSI
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header avec animation */}
          <div className="bg-gradient-to-r from-green-400 to-green-600 p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-bounce">
                <CheckCircle size={80} className="text-white" strokeWidth={2.5} />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Paiement Réussi ! 🎉
            </h1>
            <p className="text-green-100 text-lg">
              Votre abonnement TeckiA a été activé avec succès
            </p>
          </div>

          {/* Contenu */}
          <div className="p-8">
            {/* Détails de la transaction */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                📋 Détails de la transaction
              </h2>
              
              <div className="space-y-3">
                {orderId && (
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Numéro de commande</span>
                    <span className="font-mono text-sm font-semibold text-gray-800">
                      {orderId}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-600">Statut</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    ✅ {paymentStatus?.status === 'completed' ? 'Confirmé' : 'Réussi'}
                  </span>
                </div>
                
                {(amount || paymentStatus?.amount) && (
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="text-gray-600">Montant payé</span>
                    <span className="font-bold text-green-600 text-lg">
                      {Number(amount || paymentStatus?.amount).toLocaleString()} Ar
                    </span>
                  </div>
                )}
                
                {txnid && (
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="text-gray-600">ID Transaction</span>
                    <span className="font-mono text-xs text-gray-600">{txnid}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Méthode de paiement</span>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">OM</span>
                    </div>
                    <span className="font-semibold text-gray-800">Orange Money</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations importantes */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Que se passe-t-il maintenant ?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Vos crédits ont été ajoutés à votre compte</li>
                      <li>Vous pouvez immédiatement utiliser vos services</li>
                      <li>Un email de confirmation a été envoyé</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleGoToDashboard}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
              >
                Aller au Dashboard
                <ArrowRight size={20} />
              </button>
              
              <button
                onClick={handleViewInvoice}
                className="flex-1 bg-white border-2 border-gray-300 text-gray-700 font-bold py-4 px-6 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Download size={20} />
                Télécharger la facture
              </button>
            </div>

            {/* Compte à rebours */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Redirection automatique vers le dashboard dans{' '}
                <span className="font-bold text-purple-600">{countdown}</span> secondes...
              </p>
            </div>
          </div>
        </div>

        {/* Message de support */}
        <div className="mt-6 text-center">
          <p className="text-white text-sm">
            Besoin d'aide ? Contactez notre support :{' '}
            <a href="mailto:contact@tdidigital.io" className="underline hover:text-purple-200">
              contact@tdidigital.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;